import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Trans } from "@lingui/macro";
import PropTypes from "prop-types";
import shallow from "zustand/shallow";
import { pushNotificationClient } from "@base/common/clients/push_notification";
import { rtdbClient } from "@base/common/clients/rtdb";
import { CacheService } from "@base/common/libs/services";
import { clearDelegationData } from "@base/common/libs/utils";
import { useAccountStore, useUserStore } from "@base/common/libs/stores";
import {
  ENVIRONMENT_TYPES,
  PRODUCTS,
  ACCOUNT_TYPES,
  WORK_PLATFORM_FEATURE_FLAGS,
  EXTENDED_MANAGEMENT_FEATURE_FLAGS,
  ADMIN_ACTIONS,
  ADMIN_RESOURCES,
  ADMIN_MODULE,
  ANALYTICS_FEATURE_FLAGS
} from "@base/common/constants";

import { KfIcon, KF_ICONS, ICON_SIZE, ICON_STROKE } from "widgets/kficon";
import { Menu, MenuDivider, MenuItem, SubMenu } from "widgets/menu";
import { LazyLoadComponent } from "widgets/lazyloader";
import { Link } from "widgets/navigation";
import { hasFeatures } from "widgets/others";
import { Popover, POPOVER_POSITIONS, POPOVER_TRIGGERS } from "widgets/popover";
import { UserIcon, AVATAR_SIZE } from "widgets/datadisplay";

import {
  hasPermissions,
  canAccessAccountAdministration
} from "@base/common/accessctrl/index.js";
import { ROUTES_WITH_BACK_NAV } from "../../constants.js";

import styles from "../styles.css";

const AnalyticsIcon = () => (
  <KfIcon
    icon={KF_ICONS.CHART}
    size={ICON_SIZE.SIZE_16}
    stroke={ICON_STROKE.LIGHT}
  />
);

UserProfile.propTypes = {
  currentClosePopup: PropTypes.node,
  logOutUrl: PropTypes.string
};
export function UserProfile({
  currentClosePopup,
  logOutUrl = `/signin/2/${window.accountDetails._id}/logout`
}) {
  const { currentUser, sandbox } = useUserStore(
    (state) => ({
      currentUser: state.currentUser,
      sandbox: state.sandbox
    }),
    shallow
  );
  const isSuperAdmin = useUserStore.getState().isSuperAdmin();
  const { isEmployeeAdmin, isExtendedUserManagement, isMemberAdmin } =
    useAccountStore((state) => ({
      isEmployeeAdmin: state.isEmployeeAdmin,
      isExtendedUserManagement: state.isExtendedUserManagement,
      isMemberAdmin: state.isMemberAdmin
    }));

  const canAccessEnvironments = hasPermissions({
    module: ADMIN_MODULE,
    resource: ADMIN_RESOURCES.ENVIRONMENTS,
    actions: [ADMIN_ACTIONS.READ]
  });

  const canAccessServiceAccount = hasPermissions({
    module: ADMIN_MODULE,
    resource: ADMIN_RESOURCES.SERVICE_ACCOUNT,
    actions: [ADMIN_ACTIONS.READ]
  });

  const canAccessGovernance = hasPermissions({
    module: ADMIN_MODULE,
    resource: ADMIN_RESOURCES.GOVERNANCE,
    actions: [ADMIN_ACTIONS.USER, ADMIN_ACTIONS.GROUP, ADMIN_ACTIONS.FLOW]
  });

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [currentEnv, setCurrentEnv] = useState({
    _id: useAccountStore.getState()._id,
    Type: ENVIRONMENT_TYPES.PRODUCTION
  });
  const [isThemeOpen, setThemeOpen] = useState(false);
  const [serviceAccountList, setServiceAccountList] = useState([]);

  const isPortal = window.accountDetails.IsPortal;

  useEffect(function initialFunctionCall() {
    if (
      hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.ADMIN_ALLOW_SERVICE_ACCOUNT]) &&
      !canAccessServiceAccount
    ) {
      getServiceAccount();
    }
  }, []);

  useEffect(
    function onSandboxload() {
      if (sandbox?.length > 0) {
        const currEnv = sandbox.find(
          (env) => env._id === useAccountStore.getState()._id
        );
        setCurrentEnv(currEnv);
      }
    },
    [sandbox]
  );

  const setPreviousRoute = () => {
    if (
      !ROUTES_WITH_BACK_NAV.some((path) =>
        window.location.pathname.includes(path)
      )
    ) {
      window._previousRoute = window.location.pathname;
    }
  };

  const closeThemeModal = (e) => {
    e.stopPropagation();
    setThemeOpen(false);
  };

  const isIntegrationEnabled = () => {
    const flowControl = currentUser.AccountDetails.FlowControl;
    return !(
      flowControl &&
      flowControl[PRODUCTS.INTEGRATION] &&
      flowControl[PRODUCTS.INTEGRATION].IsDisabled
    );
  };

  const isDeveloperPlatformEnabled = () => {
    return (
      useAccountStore.getState().AccountType >=
        ACCOUNT_TYPES.DEVELOPMENT_ACCOUNT && isSuperAdmin
    );
  };

  const isAnalyticsPreferenceEnabled = useMemo(
    function analyticsPreference() {
      return (
        currentUser.AccountDetails.AnalyticsPreference &&
        currentUser.AccountDetails.AnalyticsPreference.Status === "Active"
      );
    },
    [currentUser]
  );

  const isAnalyticsEnabled = () => {
    return (
      // Anlytics user profile menu will only appear for kpc when left nav not availble
      !hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.KISSFLOW_ALLOW_LEFT_NAV]) &&
      hasFeatures([ANALYTICS_FEATURE_FLAGS.REPORT_CREATE_TABLE]) &&
      (isAnalyticsPreferenceEnabled || isSuperAdmin)
    );
  };

  function handleLogout() {
    pushNotificationClient.signout();
    rtdbClient.signout();
    clearDelegationData();
    // NOTE: POSTHOG
    // We strongly recommend you call reset on logout even if you don't expect users to share a computer
    // REf: https://posthog.com/docs/getting-started/identify-users
    window.posthog?.reset();
    document.location.href = logOutUrl;
  }

  function getServiceAccount() {
    const fetchServiceAccountList = CacheService.get({
      url: `/user/2/${window.accountDetails._id}/serviceaccount`,
      key: "ServiceAccountList"
    });
    fetchServiceAccountList
      .onResponse((serviceAccountList) => {
        setServiceAccountList(serviceAccountList);
      })
      .onError((error) =>
        console.info(error, "Error while getting ServiceaccountList")
      );
  }

  const onOpen = () => {
    setProfileMenuOpen(true);
  };

  const onClose = () => {
    setProfileMenuOpen(false);
  };

  return (
    <div className={styles.profile} id="profile">
      <Popover
        className={styles.profilemenu}
        position={POPOVER_POSITIONS.BOTTOM_RIGHT}
        destroyOnClose={false}
        isOpen={profileMenuOpen}
        trigger={POPOVER_TRIGGERS.CLICK}
        onOpen={onOpen}
        onClose={onClose}
        targetRenderer={({ closePopup }) => {
          currentClosePopup.current = closePopup;
          return (
            <div
              className={`${profileMenuOpen && styles.activeIcon} ${
                styles.userIcon
              }`}
            >
              <UserIcon
                userId={currentUser._id}
                name={currentUser.Name}
                size={AVATAR_SIZE.SIZE36}
              />
            </div>
          );
        }}
      >
        <Menu
          onChange={() => {
            onClose();
            currentClosePopup.current && currentClosePopup.current();
          }}
        >
          {currentEnv?.Type === ENVIRONMENT_TYPES.PRODUCTION &&
            isExtendedUserManagement && (
              <MenuItem className={styles.profilemenuitem} data-menu="profile">
                <Link
                  className={`base ${styles.iconMenuText}`}
                  route={`/account/user/profile/${currentUser._id}`}
                >
                  <KfIcon
                    icon={KF_ICONS.USER_INFO}
                    size={ICON_SIZE.SIZE_16}
                    stroke={ICON_STROKE.LIGHT}
                  />
                  <Trans>My profile</Trans>
                </Link>
              </MenuItem>
            )}
          <MenuItem
            className={styles.profilemenuitem}
            onMouseDown={setPreviousRoute}
            data-menu="settings"
          >
            <Link
              className={`base ${styles.iconMenuText}`}
              route={"/mysettings/generalsettings"}
            >
              <KfIcon
                icon={KF_ICONS.USER_SETTINGS}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.LIGHT}
              />
              <Trans>My settings</Trans>
            </Link>
          </MenuItem>
          <MenuItem
            className={styles.profilemenuitem}
            onClick={() => setThemeOpen(true)}
            data-menu="themes"
          >
            <div className={`base ${styles.menuItem} ${styles.iconMenuText}`}>
              <KfIcon
                icon={KF_ICONS.COLOR}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.LIGHT}
              />
              <Trans>Themes</Trans>
            </div>
            <LazyLoadComponent
              component={() =>
                import(/* webpackPrefetch: true */ "account/themeModal")
              }
              componentName="Theme"
              open={isThemeOpen}
              onClose={closeThemeModal}
            />
          </MenuItem>
          {currentEnv?.Type === ENVIRONMENT_TYPES.PRODUCTION &&
            isIntegrationEnabled() &&
            !isPortal && (
              <MenuItem
                className={styles.profilemenuitem}
                onMouseDown={setPreviousRoute}
                data-menu="integrations"
              >
                <Link
                  className={`base ${styles.iconMenuText}`}
                  route="/integration/integrations"
                >
                  <KfIcon
                    icon={KF_ICONS.PLUG}
                    size={ICON_SIZE.SIZE_12}
                    stroke={ICON_STROKE.LIGHT}
                  />
                  <Trans>Integrations</Trans>
                </Link>
              </MenuItem>
            )}
          {isDeveloperPlatformEnabled() && (
            <SubMenu
              title={
                <div className={styles.iconMenuText}>
                  <KfIcon
                    icon={KF_ICONS.BRACKET}
                    size={ICON_SIZE.SIZE_16}
                    stroke={ICON_STROKE.LIGHT}
                  />
                  <Trans>Developer platform</Trans>
                </div>
              }
              data-menu="developerplatform"
            >
              {isIntegrationEnabled() && (
                <MenuItem
                  className={styles.profilemenuitem}
                  data-menu="connectorbuilder"
                >
                  <Link
                    className={`base ${styles.iconMenuText}`}
                    route="/integration/connector"
                  >
                    <KfIcon
                      icon={KF_ICONS.CONNECT}
                      size={ICON_SIZE.SIZE_16}
                      stroke={ICON_STROKE.LIGHT}
                    />
                    <Trans>Connector builder</Trans>
                  </Link>
                </MenuItem>
              )}

              <MenuItem className={styles.profilemenuitem} data-menu="appstore">
                <Link
                  className={`base ${styles.iconMenuText}`}
                  route="/developerplatform/appstore"
                >
                  <KfIcon
                    icon={KF_ICONS.APP_STORE_BUILDER}
                    size={ICON_SIZE.SIZE_16}
                    stroke={ICON_STROKE.LIGHT}
                  />
                  <Trans>App Store builder</Trans>
                </Link>
              </MenuItem>
            </SubMenu>
          )}
          {/* <SubMenu title="Theme" className={styles.themestyle}>
                <MenuItem
                  onClick={() => {
                    switchTheme("auto");
                  }}
                >
                  Auto
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    switchTheme("light");
                  }}
                >
                  Light
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    switchTheme("dark");
                  }}
                >
                  Dark
                </MenuItem>
              </SubMenu> */}
          {isIntegrationEnabled() && <MenuDivider />}
          {hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.APP_ALLOW_APP_BUILDER]) &&
            canAccessEnvironments &&
            currentEnv?.Type === ENVIRONMENT_TYPES.PRODUCTION && (
              // TODO - added temporarily for enabling Environments
              <>
                <MenuItem
                  className={styles.profilemenuitem}
                  data-menu="environments"
                >
                  <Link
                    className={`base ${styles.iconMenuText}`}
                    route="/application/environment"
                  >
                    <KfIcon
                      icon={KF_ICONS.ALL_ITEMS}
                      size={ICON_SIZE.SIZE_16}
                      stroke={ICON_STROKE.LIGHT}
                    />
                    <Trans>Environments</Trans>
                  </Link>
                </MenuItem>
              </>
            )}
          {isExtendedUserManagement && (
            <Fragment>
              {currentEnv?.Type === ENVIRONMENT_TYPES.PRODUCTION &&
                (isEmployeeAdmin ||
                  (isSuperAdmin &&
                    hasFeatures([
                      EXTENDED_MANAGEMENT_FEATURE_FLAGS.ADMIN_ALLOW_EMPLOYEE_MANAGEMENT
                    ]))) && (
                  <MenuItem
                    className={styles.profilemenuitem}
                    data-menu="employeemanagement"
                  >
                    <Link
                      className={`base ${styles.iconMenuText}`}
                      route="/account/employeemanagement/employees"
                    >
                      <KfIcon
                        icon={KF_ICONS.USER_BATCH}
                        size={ICON_SIZE.SIZE_16}
                        stroke={ICON_STROKE.LIGHT}
                      />
                      <Trans>Employee management</Trans>
                    </Link>
                  </MenuItem>
                )}

              {(isMemberAdmin ||
                (isSuperAdmin &&
                  hasFeatures([
                    EXTENDED_MANAGEMENT_FEATURE_FLAGS.ADMIN_ALLOW_MEMBER_MANAGEMENT
                  ]))) && (
                <MenuItem
                  className={styles.profilemenuitem}
                  data-menu="membermanagement"
                >
                  <Link
                    className={`base ${styles.iconMenuText}`}
                    route="/account/membermanagement/members"
                  >
                    <KfIcon
                      icon={KF_ICONS.USER_GROUP}
                      size={ICON_SIZE.SIZE_16}
                      stroke={ICON_STROKE.LIGHT}
                    />
                    <Trans>Member management</Trans>
                  </Link>
                </MenuItem>
              )}
            </Fragment>
          )}
          {(canAccessAccountAdministration() || serviceAccountList.length) && (
            <MenuItem
              className={styles.profilemenuitem}
              onMouseDown={setPreviousRoute}
              data-menu="administration"
            >
              <Link
                className={`base ${styles.iconMenuText}`}
                route="/account/admin"
              >
                <KfIcon
                  icon={KF_ICONS.TOOLS}
                  size={ICON_SIZE.SIZE_16}
                  stroke={ICON_STROKE.LIGHT}
                />
                <Trans>Account administration</Trans>
              </Link>
            </MenuItem>
          )}
          {canAccessGovernance &&
            hasFeatures([
              WORK_PLATFORM_FEATURE_FLAGS.ACCOUNT_ALLOW_ADMIN_GOVERNANCE,
              WORK_PLATFORM_FEATURE_FLAGS.ADMIN_ALLOW_ACCOUNT_GOVERNANCE
            ]) && (
              <MenuItem
                className={styles.profilemenuitem}
                onMouseDown={setPreviousRoute}
                data-menu="governance"
              >
                <Link
                  className={`base ${styles.iconMenuText}`}
                  route="/account/governance"
                >
                  <KfIcon
                    icon={KF_ICONS.GOVERNANCE}
                    size={ICON_SIZE.SIZE_16}
                    stroke={ICON_STROKE.LIGHT}
                  />
                  <Trans>Account governance</Trans>
                </Link>
              </MenuItem>
            )}

          {isAnalyticsEnabled() && (
            <>
              <MenuDivider />
              <MenuItem
                className={styles.profilemenuitem}
                data-menu="analytics"
              >
                <Link
                  className={`base ${styles.iconMenuText}`}
                  route="/analytics"
                >
                  <AnalyticsIcon />
                  <Trans>Analytics</Trans>
                </Link>
              </MenuItem>
            </>
          )}
          <MenuDivider />
          <MenuItem className={styles.profilemenuitem} data-menu="signout">
            <Link
              className={`base ${styles.iconMenuText}`}
              target="_self"
              onClick={handleLogout}
            >
              <KfIcon
                icon={KF_ICONS.LOGOUT}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.LIGHT}
                color="--kf-color-error-500"
              />
              <p className={styles.signOut}>
                <Trans>Sign out</Trans>
              </p>
            </Link>
          </MenuItem>
        </Menu>
      </Popover>
    </div>
  );
}
